import { useEffect, useMemo, useState } from 'react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useToast } from '@/shared/components/Toast';
import {
  SpecialtyCoverageEditor,
  normalizeEditorSpecialty,
  toSpecialtyCoveragePayload,
  toConfiguredCoveragePayload,
  type SpecialtyCoverageItem,
  type SpecialtyOption,
} from '@/shared/components/specialty-coverage';
import {
  useSpecialtyCoverage,
  useUpdateSpecialtyCoverage,
} from '../hooks/useSpecialtyCoverage';

type SpecialtyCoverageSectionProps = {
  specialtyOptions: SpecialtyOption[];
  /** Bubbles unsaved coverage edits so the profile tab can guard navigation. */
  onDirtyChange?: (dirty: boolean) => void;
};

function parseAxiosMessage(error: unknown): string {
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    error.response.data &&
    typeof error.response.data === 'object' &&
    'message' in error.response.data
  ) {
    const message = (error.response.data as { message: unknown }).message;
    if (typeof message === 'string') return message;
    if (Array.isArray(message)) return message.join('. ');
  }
  if (error instanceof Error) return error.message;
  return 'Failed to save coverage';
}

function parseFieldErrors(
  message: string,
  specialties: SpecialtyCoverageItem[],
): Record<string, string> {
  const match = message.match(/specialties\[(\d+)\](?:\.\S+)?:\s*(.*)/i);
  if (!match) return {};
  const specialty = specialties[Number(match[1])];
  if (!specialty) return {};
  return { [specialty.specialtyId]: match[2] || message };
}

/** SP self-service coverage editor — coverage/ranges only (no specialty add/remove). */
export function SpecialtyCoverageSection({
  specialtyOptions,
  onDirtyChange,
}: SpecialtyCoverageSectionProps) {
  const { showToast } = useToast();
  const { data, isLoading, isError } = useSpecialtyCoverage();
  const updateCoverage = useUpdateSpecialtyCoverage();

  const [items, setItems] = useState<SpecialtyCoverageItem[]>([]);
  const [baseline, setBaseline] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!data?.specialties) return;
    const normalized = data.specialties.map((item) => normalizeEditorSpecialty(item));
    setItems(normalized);
    setBaseline(JSON.stringify(toSpecialtyCoveragePayload(normalized)));
    setFieldErrors({});
    setSaveError('');
  }, [data]);

  const isDirty = useMemo(
    () => JSON.stringify(toSpecialtyCoveragePayload(items)) !== baseline,
    [items, baseline],
  );

  // Unconfigured rows (coverageScope null — e.g. attached via Admin create by
  // specialtyIds, issue 15/24) block save until the SP picks scope + a mode.
  const hasUnconfigured = items.some(
    (item) => item.coverageScope === null || item.serviceRanges.length === 0,
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const handleSave = async () => {
    setSaveError('');
    setFieldErrors({});
    try {
      const payload = { specialties: toConfiguredCoveragePayload(items) };
      const result = await updateCoverage.mutateAsync(payload);
      const normalized = result.specialties.map((item) => normalizeEditorSpecialty(item));
      setItems(normalized);
      setBaseline(JSON.stringify(toSpecialtyCoveragePayload(normalized)));
      showToast('Coverage updated successfully!', 'success');
    } catch (error) {
      const message = parseAxiosMessage(error);
      setSaveError(message);
      setFieldErrors(parseFieldErrors(message, items));
      showToast('Failed to update coverage. Please try again.', 'error');
    }
  };

  if (isLoading) {
    return (
      <Card className="space-y-3 p-6" data-testid="specialty-coverage-skeleton">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-full max-w-md" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="p-6">
        <p className="text-sm text-red-600" role="alert">
          Failed to load specialty coverage. Please refresh and try again.
        </p>
      </Card>
    );
  }

  return (
    <Card className="space-y-4 p-6">
      <SpecialtyCoverageEditor
        value={items}
        onChange={(next) => {
          setItems(next);
          setFieldErrors({});
          setSaveError('');
        }}
        specialtyOptions={specialtyOptions}
        permissions={{ canManageAttachments: false }}
        fieldErrors={fieldErrors}
        disabled={updateCoverage.isPending}
      />

      {saveError ? (
        <p className="text-sm text-red-600" role="alert">
          {saveError}
        </p>
      ) : null}

      <div className="flex items-center justify-end gap-4 border-t border-border-subtle pt-4">
        <p
          className={`text-sm text-text-tertiary transition-opacity duration-200 ${
            isDirty ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden={!isDirty}
        >
          Unsaved changes
        </p>
        <Button
          type="button"
          onClick={handleSave}
          className="bg-bquest-green text-white hover:bg-bquest-green/90"
          disabled={!isDirty || updateCoverage.isPending || items.length === 0 || hasUnconfigured}
        >
          {updateCoverage.isPending ? 'Saving…' : 'Save coverage'}
        </Button>
      </div>
    </Card>
  );
}
